import React from 'react';

/**
 * Segmented pill-toggle used for Active/Archived tabs and the tile/list
 * view toggle on the dashboard. Per handoff README:
 *   container — bg-surface-2, 1px border-subtle, r=7, 2px padding
 *   active item — bg-surface + shadow-xs, text-1, weight 500
 *   inactive — transparent, text-3
 *
 * Options is an array of { value, label, count?, icon? }. When `icon` is
 * provided, the option renders as an icon-only square (compact mode);
 * otherwise it renders as a text pill (label + optional count).
 *
 *   <SegmentedControl
 *     value={mode}
 *     onChange={setMode}
 *     options={[
 *       { value: 'active', label: 'Active', count: 7 },
 *       { value: 'archived', label: 'Archived', count: 0 },
 *     ]}
 *   />
 */
export default function SegmentedControl({
  value,
  onChange,
  options,
  compact = false,
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 7,
        padding: 2,
        gap: 2,
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            title={opt.title || opt.label}
            style={{
              border: 'none',
              cursor: 'pointer',
              background: active ? 'var(--bg-surface)' : 'transparent',
              boxShadow: active ? 'var(--shadow-xs)' : 'none',
              color: active ? 'var(--text-1)' : 'var(--text-3)',
              fontWeight: 500,
              fontSize: 14,
              padding: compact ? '4px 6px' : '5px 12px',
              borderRadius: 5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              transition: 'background .12s, color .12s',
              minHeight: compact ? 24 : 'auto',
            }}
          >
            {opt.icon}
            {opt.label && <span>{opt.label}</span>}
            {typeof opt.count === 'number' && (
              <span
                className="mono"
                style={{
                  fontSize: 12.5,
                  color: active ? 'var(--text-3)' : 'var(--text-4)',
                }}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
